<script setup lang="ts">
import type { PartState } from '@/types/rc/work-orders'
import { PartStatus } from '@/types/rc/miners/PartStatus'
import { computed } from 'vue'
import { Icon } from '@iconify/vue'
import { h, type VNode } from 'vue'

/** Tooltip placement values accepted by el-tooltip (matches Element Plus Placement) */
type TooltipPlacement =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'left-start'
  | 'left-end'
  | 'right'
  | 'right-start'
  | 'right-end'

const props = defineProps<{
  partState?: PartState
  tooltipPlacement?: TooltipPlacement
}>()

const statusIcon = computed(() => PartStateToIcon(props.partState?.status ?? undefined))

function PartStateToIcon(status?: string): VNode {
  if (!status) {
    return h(Icon, {
      icon: 'material-symbols:remove',
      height: '20',
    })
  }

  switch (status) {
    case PartStatus.Good:
      return h(Icon, {
        icon: 'material-symbols:check',
        style: 'color: var(--fill-success)',
        height: '20',
      })
    case PartStatus.Fail:
      return h(Icon, {
        icon: '',
        style: 'color: var(--fill-error); border: 1px solid var(--component-stroke-gray)',
        height: '20',
      })
    case PartStatus.Irreparable:
      return h(Icon, {
        icon: 'material-symbols:pen-size-2',
        style: 'color: var(--fill-error); border: 1px solid var(--component-stroke-gray)',
        height: '20',
      })
    case PartStatus.Unrepairable:
      return h(Icon, {
        icon: 'material-symbols:close',
        style: 'color: var(--fill-error); border: 1px solid var(--component-stroke-gray)',
        height: '20',
      })
    case PartStatus.Replaced:
      return h(Icon, {
        icon: 'material-symbols:check',
        style: 'color: var(--fill-success); border: 1px solid var(--component-stroke-gray)',
        height: '20',
      })
    case PartStatus.Repaired:
      return h(Icon, {
        icon: 'material-symbols:check',
        style: 'color: var(--fill-success); border: 1px solid var(--component-stroke-gray)',
        height: '20',
      })
    case PartStatus.Blocked:
      return h(Icon, {
        icon: 'material-symbols:exclamation',
        style: 'color: var(--fill-error); border: 1px solid var(--component-stroke-gray)',
        height: '20',
      })
    case PartStatus.RepairNotAuthorized:
      return h(Icon, {
        icon: 'material-symbols:gpp-bad-outline',
        style: 'color: var(--fill-error); border: 1px solid var(--component-stroke-gray)',
        height: '20',
      })
    default:
      return h(Icon, {
        icon: 'material-symbols:question-mark',
        style: 'color: var(--text-invert)',
        height: '20',
      })
  }
}
</script>

<template>
  <template v-if="!props.partState"></template>
  <template v-else>
    <el-tooltip effect="dark" :placement="props.tooltipPlacement ?? 'bottom'">
      <template #content>
        <div v-if="!props.partState.status">Status not set</div>
        <div v-else-if="props.partState.status == PartStatus.Fail" style="text-align: center">
          {{ props.partState.status }} <br />
          <i>{{ props.partState.failure_type }}</i>
        </div>
        <div v-else>{{ props.partState.status }}</div>
      </template>
      <component :is="statusIcon" />
    </el-tooltip>
  </template>
</template>
